import type { Disposer, ExternalStatus, ViewExternalApi } from '@hibro/shared';
import { externalProcessKey } from '@hibro/shared';

import type { ExternalBridge } from './external-surface';

/** Snapshot reported when no external bridge is wired (e.g. web build). */
const OFFLINE: ExternalStatus = { running: false };

/**
 * Build the {@link ViewExternalApi} handed to a renderer view as
 * `ViewProps.external` (架构方案 §3.1/§5.8), letting a module paint its own
 * control panel for its external processes.
 *
 * The module-scoping lives here: callers pass an `entry.external` **entry id**
 * and this composes the process key from the *hosting view's* `moduleId`, so a
 * module can only ever address its own processes — no key is accepted from
 * module code. Degrades to a not-running/no-op API when the shell injected no
 * bridge, mirroring `ViewProps.detach`.
 */
export function createViewExternalApi(
  moduleId: string,
  bridge?: ExternalBridge,
): ViewExternalApi {
  const key = (entryId?: string): string =>
    externalProcessKey(moduleId, entryId);
  return {
    launch: (entryId) =>
      bridge?.launch(key(entryId)) ?? Promise.resolve(OFFLINE),
    focus: (entryId) => bridge?.activate(key(entryId)) ?? Promise.resolve(),
    close: (entryId) => bridge?.close(key(entryId)) ?? Promise.resolve(),
    status: (entryId) =>
      bridge?.status(key(entryId)) ?? Promise.resolve(OFFLINE),
    onDidChangeStatus: (listener, entryId): Disposer => {
      if (!bridge?.onDidChange) {
        return () => undefined;
      }
      const processKey = key(entryId);
      return bridge.onDidChange(() => {
        void bridge.status(processKey).then(listener, () => undefined);
      });
    },
  };
}
